import { NetworkStatus, useApolloClient } from '@apollo/client';
import { Box } from '@mui/material';
import {
  GridExpandMoreIcon,
  GridGetRowsError,
  type GridRenderCellParams,
  type GridRowParams,
  type GridValidRowModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgButton } from 'src/components/ui';
import { useAvailableHeight } from 'src/hooks/useAvailableHeight';
import useGridExport, { useGridExportState } from 'src/hooks/useGridExport';
import { notify } from 'src/lib/notify';
import { DeferredRetry } from 'src/modules/entities/deferred';
import { DataGrid, type DataSourceDataGridProps, GridToolbarV2, useGridRows } from 'src/modules/grids';
import BulkEditCostBasis from 'src/modules/settings/business/pricingSheets/components/BulkEditCostBasis';
import createCustomFieldColumns from 'src/utils/grid/column/createCustomFieldColumns';
import { AddProductDrawer } from './AddProductDrawer';
import {
  type CatalogDataSource,
  type CatalogScope,
  COMMODITY_KEY_PREFIX,
  commodityGroupKey,
  createCatalogDataSource,
  isCommodityRow,
} from './catalogDataSource';
import { CATALOG_STORAGE_KEY, COMMODITY_PAGE_SIZE, useCatalogRootSeed } from './catalogRootSeed';
import { useCatalogTransport } from './hooks/useCatalogTransport';
import { COMMODITY_CATALOG_QUERY } from './hooks/useCommodities';
import { getRowsWithoutGroups, useCommoditiesColumns } from './hooks/useCommoditiesColumns';
import type { CatalogGridRow } from './hooks/useCommoditiesPage';
import ProductReservationWarningModal from './ProductReservationWarningModal';
import type { ProductReservation } from './product.api';
import { useFindProductReservations, useUpdateProduct } from './product.api';

const ROOT_PAGE_GENERATION = Symbol('catalogRootPageGeneration');
const INITIAL_RESERVATION_DISPLAY_LIMIT = 10;
/** Products per batch inside one expanded commodity. */
const PRODUCT_BATCH_SIZE = 100;
/** A search opens this many matching commodities at once: bounded, never a fetch-all waterfall. */
const SEARCH_AUTO_OPEN_LIMIT = 5;
/** Data sources kept per recent scope, so clearing a search returns to already-loaded windows. */
const RECENT_SCOPE_LIMIT = 5;

type CatalogRootGenerationRow = CatalogGridRow & { [ROOT_PAGE_GENERATION]?: symbol };

type WindowInfo = { shown: number; total: number; hasMore: boolean };

const isCommodityGroupKey = (id: string) => id.startsWith(COMMODITY_KEY_PREFIX);

/**
 * Every catalog mutation — create, edit, disable, bulk cost basis — refetches COMMODITY_CATALOG_QUERY.
 * Each completed refetch is one invalidation signal for every write path, so the catalog refreshes
 * its headers (counts) and every open group without each call site wiring it. Subscribed on the
 * observable, not read off a render: React batches `loading → ready` into one render.
 */
function useCatalogInvalidation(): number {
  const client = useApolloClient();
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let refetching = false;
    const subscription = client
      .watchQuery({ query: COMMODITY_CATALOG_QUERY, notifyOnNetworkStatusChange: true })
      .subscribe(({ networkStatus }) => {
        if (networkStatus === NetworkStatus.refetch) refetching = true;
        if (networkStatus === NetworkStatus.ready && refetching) {
          refetching = false;
          setVersion((current) => current + 1);
        }
      });
    return () => subscription.unsubscribe();
  }, [client]);
  return version;
}

const sortReservations = (reservations: readonly ProductReservation[]) => {
  return [...reservations].sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
};

export default function CommoditiesGrid() {
  const apiRef = useGridApiRef();
  const flags = useFlags();
  const [editingProduct, setEditingProduct] = useState<CatalogGridRow | null>(null);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'duplicate'>('create');
  const [warningModal, setWarningModal] = useState<{
    productId: number;
    disabled: boolean;
    reservations: ProductReservation[];
    hasMore: boolean;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const availableHeight = useAvailableHeight(containerRef);

  const enhancedGridState = useGridRows({ mode: 'paged', storageKey: CATALOG_STORAGE_KEY });
  // MUI's print widens the page to every row. On a data-source grid that page change refetches the root,
  // which clears the rows MUI is about to clone, so pagination is off while a print is in flight.
  const isPrinting = useGridExportState((state) => state.exportFormat === 'print');
  const invalidation = useCatalogInvalidation();

  const scope = useMemo<CatalogScope>(
    () => ({
      searchText: enhancedGridState.searchText || undefined,
      gridFilters: enhancedGridState.gridFilters,
      quickFilters: enhancedGridState.quickFilterOption?.filters ?? undefined,
    }),
    [enhancedGridState.searchText, enhancedGridState.gridFilters, enhancedGridState.quickFilterOption?.filters]
  );
  const { transport, reader } = useCatalogRootSeed(useCatalogTransport(), scope);
  const scopeKey = JSON.stringify(scope);
  const [retryNonce, setRetryNonce] = useState(0);
  const sourceKey = `${scopeKey}#${invalidation}#${retryNonce}`;

  const [windows, setWindows] = useState<Record<string, WindowInfo>>({});
  const [rootError, setRootError] = useState<Error>();
  const [loadedSourceKey, setLoadedSourceKey] = useState<string>();
  const expandedByScope = useRef(new Map<string, Set<string>>());
  const restorePending = useRef<{ sourceKey: string; generation: symbol } | null>(null);
  const currentSourceKey = useRef(sourceKey);
  currentSourceKey.current = sourceKey;
  const sources = useRef(new Map<string, CatalogDataSource>());
  const sourceInvalidation = useRef(invalidation);

  const dataSource = useMemo(() => {
    if (sourceInvalidation.current !== invalidation) {
      // A write changed the catalog: every cached window and count is stale, not just this scope's.
      sources.current.clear();
      sourceInvalidation.current = invalidation;
    }
    const cached = sources.current.get(sourceKey);
    if (cached) return cached;

    const created = createCatalogDataSource({
      transport,
      scope,
      rootPageSize: COMMODITY_PAGE_SIZE,
      childPageSize: PRODUCT_BATCH_SIZE,
      onRootLoaded: (headers) => {
        if (currentSourceKey.current !== sourceKey) return;
        const generation = Symbol();
        for (const header of headers) Object.assign(header, { [ROOT_PAGE_GENERATION]: generation });
        restorePending.current = { sourceKey, generation };
        setLoadedSourceKey(sourceKey);
      },
      onWindowChanged: (groupKey, window) => setWindows((current) => ({ ...current, [groupKey]: window })),
    });
    sources.current.set(sourceKey, created);
    while (sources.current.size > RECENT_SCOPE_LIMIT) {
      const oldest = sources.current.keys().next().value;
      if (oldest === undefined) break;
      sources.current.delete(oldest);
    }
    return created;
  }, [invalidation, scope, sourceKey, transport]);

  // A reused data source already holds its windows; a new one starts empty.
  useEffect(() => {
    setRootError(undefined);
    const next: Record<string, WindowInfo> = {};
    for (const header of dataSource.headers()) {
      const window = dataSource.window(header.id);
      if (window) next[header.id] = window;
    }
    setWindows(next);
  }, [dataSource]);

  // Remember which commodities are open, per scope, from expansion events as they happen. The scope is
  // read through a ref set during render, so an event is filed under the scope that was current then.
  // (A snapshot at switch time is too late: MUI's own layout effects rebuild the tree first.)
  const currentScopeKey = useRef(scopeKey);
  currentScopeKey.current = scopeKey;
  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;
    return api.subscribeEvent('rowExpansionChange', (node) => {
      if (typeof node.id !== 'string' || !isCommodityGroupKey(node.id)) return;
      const expanded = expandedByScope.current.get(currentScopeKey.current) ?? new Set<string>();
      if (node.childrenExpanded) expanded.add(node.id);
      else expanded.delete(node.id);
      expandedByScope.current.set(currentScopeKey.current, expanded);
    });
  }, [apiRef]);

  // After a root page lands: reopen what was open in this scope, or — for a search nobody has
  // browsed yet — open the first few matching commodities so results show without a click.
  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;
    return api.subscribeEvent('rowsSet', () => {
      const pending = restorePending.current;
      if (!pending || pending.sourceKey !== sourceKey) return;
      if (
        dataSource
          .headers()
          .some(
            (header) => api.getRow<CatalogRootGenerationRow>(header.id)?.[ROOT_PAGE_GENERATION] !== pending.generation
          )
      )
        return;
      restorePending.current = null;
      const remembered = expandedByScope.current.get(scopeKey);
      const toOpen = remembered
        ? [...remembered]
        : scope.searchText
          ? dataSource
              .headers()
              .filter((header) => header.matchingProductCount > 0)
              .slice(0, SEARCH_AUTO_OPEN_LIMIT)
              .map((header) => header.id)
          : [];
      for (const id of toOpen) {
        if (api.getRowNode(id)) api.setRowChildrenExpansion(id, true);
      }
    });
  }, [apiRef, dataSource, scope.searchText, scopeKey, sourceKey]);

  const handleLoadMore = useCallback(
    async (groupKey: string) => {
      try {
        await dataSource.loadMore(groupKey);
        await apiRef.current?.dataSource.fetchRows(groupKey);
      } catch (loadError) {
        notify.error(loadError);
      }
    },
    [apiRef, dataSource]
  );

  const updateProduct = useUpdateProduct();
  const findProductReservations = useFindProductReservations();

  const handleEdit = useCallback((row: CatalogGridRow) => {
    setEditingProduct(row);
    setDrawerMode('edit');
  }, []);

  const handleDuplicate = useCallback((row: CatalogGridRow) => {
    setEditingProduct(row);
    setDrawerMode('duplicate');
  }, []);

  const handleToggleDisabled = useCallback(
    async (product: CatalogGridRow) => {
      if (typeof product.id !== 'number') {
        return;
      }

      // Check for reservations before disabling
      if (!product.disabled) {
        const { data } = await findProductReservations({ id: product.id });
        const reservations =
          data?.productReservations?.filter((reservation): reservation is ProductReservation => reservation !== null) ??
          [];

        if (reservations.length > 0) {
          // Show warning modal
          const sortedReservations = sortReservations(reservations);
          const displayReservations = sortedReservations.slice(0, INITIAL_RESERVATION_DISPLAY_LIMIT);
          const hasMore = reservations.length > INITIAL_RESERVATION_DISPLAY_LIMIT;

          setWarningModal({ productId: product.id, disabled: true, reservations: displayReservations, hasMore });
          return;
        }
      }

      // No reservations or re-enabling, proceed with update
      updateProduct(product.id, { disabled: !product.disabled });
    },
    [updateProduct, findProductReservations]
  );

  const handleCloseWarningModal = useCallback(() => {
    setWarningModal(null);
  }, []);

  const handleConfirmDisable = useCallback(() => {
    if (warningModal) {
      updateProduct(warningModal.productId, { disabled: warningModal.disabled });
      setWarningModal(null);
    }
  }, [warningModal, updateProduct]);

  const handleSeeMoreReservations = useCallback(async () => {
    if (warningModal) {
      const { data } = await findProductReservations({ id: warningModal.productId });
      const reservations =
        data?.productReservations?.filter((reservation): reservation is ProductReservation => reservation !== null) ??
        [];
      const sortedReservations = sortReservations(reservations);

      setWarningModal({ ...warningModal, reservations: sortedReservations, hasMore: false });
    }
  }, [warningModal, findProductReservations]);

  const handleCloseDrawer = useCallback(() => {
    setEditingProduct(null);
    setDrawerMode('create');
  }, []);

  const selectedRows = useMemo(
    () =>
      Object.values(enhancedGridState.selectedRows).filter(
        (row): row is { id: number } =>
          typeof row === 'object' && row !== null && 'id' in row && typeof row.id === 'number'
      ),
    [enhancedGridState.selectedRows]
  );

  const updateCostBasisState = useCallback(() => {
    // The mutation will automatically trigger a refetch via refetchQueries
    // So we don't need to manually update state here
  }, []);

  const resolveCommodityProducts = useCallback(
    async (row: GridValidRowModel): Promise<CatalogGridRow[]> => {
      try {
        if (!isCommodityRow(row)) return [];
        return await transport.fetchAllProducts({ scope, commodityId: row.commodityId });
      } catch (selectionError) {
        notify.error(selectionError);
        throw selectionError;
      }
    },
    [scope, transport]
  );

  const resolveAllProducts = useCallback(async (): Promise<CatalogGridRow[]> => {
    try {
      return await transport.fetchAllProducts({ scope });
    } catch (selectionError) {
      notify.error(selectionError);
      throw selectionError;
    }
  }, [scope, transport]);

  const fetchAllRows = useCallback(() => transport.fetchAllProducts({ scope }), [scope, transport]);

  const customFields = useMemo(() => {
    const customFieldsConfig = flags['customFields']?.product;
    return createCustomFieldColumns(customFieldsConfig);
  }, [flags]);

  const columns = useCommoditiesColumns({
    customFields,
    onEdit: handleEdit,
    onDuplicate: handleDuplicate,
    onToggleDisabled: handleToggleDisabled,
  });

  useGridExport(
    apiRef,
    'all-products',
    { getRowsToExport: getRowsWithoutGroups },
    { getRowsToExport: getRowsWithoutGroups },
    fetchAllRows
  );

  const toolbar = useMemo(
    () => (
      <GridToolbarV2
        apiRef={apiRef}
        enhancedGridState={enhancedGridState}
        entityName="Product"
        showExpandAllButton
        expandAllMode="group"
        bulkActions={<BulkEditCostBasis selectedRows={selectedRows} afterSubmit={updateCostBasisState} />}
        toolbarActionsRight={
          <AddProductDrawer mode={drawerMode} productData={editingProduct} onClose={handleCloseDrawer} />
        }
      />
    ),
    [apiRef, enhancedGridState, selectedRows, updateCostBasisState, drawerMode, editingProduct, handleCloseDrawer]
  );

  const renderGroupingCell = useCallback(
    (params: GridRenderCellParams<CatalogGridRow>) => {
      const { row } = params;
      if (!isCommodityRow(row)) {
        return <Box sx={{ pl: params.rowNode.depth * 8 }}>{row.description}</Box>;
      }

      // A commodity with nothing matching is a NAMED leaf: no expander, and not selectable.
      if (row.matchingProductCount === 0) {
        return (
          <Box component="span" sx={{ pl: 5 }}>
            {row.name} (0)
          </Box>
        );
      }

      const window = windows[row.id];
      const expanded = params.rowNode.type === 'group' && params.rowNode.childrenExpanded;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <AgButton
            variant="ghost"
            size="icon-sm"
            shape="circle"
            className="mr-4 h-7 w-7"
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${row.name}`}
            aria-expanded={expanded}
            onPress={() => apiRef.current?.setRowChildrenExpansion(row.id, !expanded)}
          >
            <GridExpandMoreIcon
              style={{
                width: '18px',
                height: '18px',
                transform: expanded ? '' : 'rotate(-90deg)',
                transition: 'transform 0.2s',
              }}
            />
          </AgButton>
          <span>
            {row.name} ({row.matchingProductCount})
          </span>
          {expanded && window && window.shown < window.total ? (
            <Box component="span" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
              <span data-testid={`catalog-window-${row.id}`}>
                Showing {window.shown} of {window.total}
              </span>
              {window.hasMore ? (
                <AgButton
                  variant="ghost"
                  size="sm"
                  aria-label={`Load more ${row.name} products, showing ${window.shown} of ${window.total}`}
                  onPress={() => void handleLoadMore(row.id)}
                >
                  Load more
                </AgButton>
              ) : null}
            </Box>
          ) : null}
        </Box>
      );
    },
    [apiRef, handleLoadMore, windows]
  );

  // Typed against the data-source contract: `rows`, `pageInfo` and the paged resolvers are
  // `never` here, so this grid cannot acquire a second row or pagination owner.
  const gridProps: DataSourceDataGridProps<CatalogGridRow> = {
    mode: 'dataSource',
    dataSource,
    enhancedGridState,
    treeData: true,
    rowsReady: loadedSourceKey === sourceKey,
    height: availableHeight,
    sx: { '& [class*="pinnedRight"]': { borderLeft: 'none !important', boxShadow: 'none !important' } },
    density: 'standard',
    columns,
    gridKey: 'settings.inventory.commodities',
    storageKey: CATALOG_STORAGE_KEY,
    apiRef,
    enableSelection: true,
    isParentRow: isCommodityRow,
    resolveChildRows: resolveCommodityProducts,
    resolveAllRows: resolveAllProducts,
    getParentRowId: (row) =>
      !isCommodityRow(row) && typeof row.commodity?.id === 'number' ? commodityGroupKey(row.commodity.id) : undefined,
    selectionScopeKey: scopeKey,
    checkboxSelectionVisibleOnly: false,
    disableColumnFilter: true,
    filterModel: { items: [] },
    isRowSelectable: (params: GridRowParams<CatalogGridRow>) =>
      !isCommodityRow(params.row) || params.row.matchingProductCount > 0,
    pagination: !isPrinting,
    pageSizeOptions: [COMMODITY_PAGE_SIZE],
    localeText: {
      paginationRowsPerPage: 'Commodities per page:',
      paginationDisplayedRows: ({ from, to, count }) => `${from}–${to} of ${count} commodities`,
    },
    initialState: {
      pagination: { paginationModel: { page: 0, pageSize: COMMODITY_PAGE_SIZE } },
      columns: {
        columnVisibilityModel: {
          weightPerInventoryUnit: false,
          variableWeight: false,
          cartonsPerPallet: false,
          productCode: false,
          isDisabled: false,
          unitOfMeasure: false,
        },
      },
    },
    pinnedColumns: { right: ['actions'] },
    groupingColDef: {
      minWidth: 300,
      flex: 2,
      headerName: 'Commodity / Product Description',
      sortable: true,
      renderCell: renderGroupingCell,
    },
    onDataSourceError: (dataSourceError) => {
      const groupKeys =
        dataSourceError instanceof GridGetRowsError && 'groupKeys' in dataSourceError.params
          ? dataSourceError.params.groupKeys
          : undefined;
      // A failed ROOT read must not render as an empty catalog that reads as "no products".
      if (dataSourceError instanceof GridGetRowsError && !(Array.isArray(groupKeys) && groupKeys.length > 0)) {
        setRootError(dataSourceError.cause ?? dataSourceError);
        return;
      }
      notify.error(dataSourceError);
    },
    slots: { toolbar },
    variant: 'list',
  };

  if (rootError) {
    return (
      <DeferredRetry
        message="Unable to load the product catalog."
        actionLabel="Retry catalog"
        onRetry={() => setRetryNonce((nonce) => nonce + 1)}
      />
    );
  }

  return (
    <>
      {reader}
      <div ref={containerRef}>
        <DataGrid<CatalogGridRow> {...gridProps} />
      </div>

      <ProductReservationWarningModal
        open={!!warningModal}
        reservations={warningModal?.reservations || []}
        hasMore={warningModal?.hasMore || false}
        onClose={handleCloseWarningModal}
        onConfirm={handleConfirmDisable}
        onSeeMore={handleSeeMoreReservations}
      />
    </>
  );
}
